import * as React from 'react';
import { CustomRequestMap, IDocumentsList } from '../../../../types';
import { CustomRequestDocumentsSponsorTypesEnum } from '../../../../types/enums';
import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import { getVisible, getDocStatus } from '../../../../utilities/documents';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';

interface IProps {
  sponsorType: CustomRequestDocumentsSponsorTypesEnum;
  documents: CustomRequestMap;
  openDocumentModal: (
    sponsorType: CustomRequestDocumentsSponsorTypesEnum,
    sponsorKey: string | null,
    docType: string,
    docKey: string,
    modalTitle: string,
    fileNames: string[],
    isComplete: boolean
  ) => void;
}

const getSectionId = (sponsorType: CustomRequestDocumentsSponsorTypesEnum): SectionId => {
  switch (sponsorType) {
    case CustomRequestDocumentsSponsorTypesEnum.BEFORE_TRAVEL:
      return SectionId.PRE_DEPARTURE_DOCS;
    case CustomRequestDocumentsSponsorTypesEnum.AFTER_ARRIVAL:
      return SectionId.AFTER_TRAVEL_DOCS;
  }
};

const CustomRequestDocument = (props: IProps) => {
  const { documents: customRequestDocuments, openDocumentModal, sponsorType } = props;
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const sectionId = getSectionId(sponsorType);
  const isSectionLocked = useIsSectionLocked(sectionId, offer.status);

  if (!customRequestDocuments || Object.keys(customRequestDocuments).length === 0) {
    return null;
  }

  const openModal = (data: { sponsorKey: string; docKey: string; fileNames: string[]; isComplete: boolean }) => {
    openDocumentModal(sponsorType, data.sponsorKey, 'customRequest', data.docKey, '', data.fileNames, data.isComplete);
  };

  let hasDocs = false;
  const docNodes: React.ReactNode[] = [];

  Object.entries(customRequestDocuments).forEach(([customKey, customDoc]) => {
    const visibleDocs = getVisible(customDoc!, false) as IDocumentsList[];

    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key: docKey, item } = doc;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                hideButton={true}
                reason={item.reason}
                key={`custom-doc-${customKey}-${docKey}`}
                id={`custom-doc-${customKey}-${docKey}`}
                status={Status.Rejected}
                label={`${item.label} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                key={`custom-doc-${customKey}-${docKey}`}
                id={`custom-doc-${customKey}-${docKey}`}
                status={getDocStatus(item.status, false, isSectionLocked)}
                label={`${item.label} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  openModal({
                    sponsorKey: customKey,
                    docKey,
                    fileNames: item.fileNames,
                    isComplete: true
                  })
                }
              />
            );
          }
        })
      );
    }
    return null;
  });

  if (hasDocs) {
    return (
      <ChecklistSection key={`custom-request-section`} label={'Custom request document(s)'}>
        {docNodes}
      </ChecklistSection>
    );
  }
  return null;
};

export default CustomRequestDocument;
